import EventBusyIcon from '@mui/icons-material/EventBusy';
import UpdateIcon from '@mui/icons-material/Update';
import { Container, Grid2 as Grid, Paper, Typography } from '@mui/material';
import moment from 'moment';
import { useContext, useEffect } from 'react';
import { displayCancellation, productURL } from '../../utils/functions/common';
import { Product } from '../../utils/models/products';
import { BookingStore } from '../../utils/stores/BookingStore';
import { ProductsStore } from '../../utils/stores/ProductsStore';
import BookingModal from '../bookings/BookingModal';
import PageTitle from '../coreLayout/PageTitle';
import Seo, { SITE_URL } from '../coreLayout/Seo';
import KeyDetailWidget from './KeyDetailWidget';
import KeySellingPointsWidget from './KeySellingPoints';
import ProductImageGallery from './ProductImageGallery';
import { DisplayText, Sales } from './TextAndPriceDisplay';

const displayProductSx = {
    productContainer: {
        pb: '25px',
        justifyContent: 'center',
        flexDirection: { xs: 'column', sm: 'row' },
    },
    productDesc: {
        padding: { xs: '0 20px', sm: '20px' },
        border: { xs: 'none', sm: '1px solid rgba(0, 0, 0, 0.12)' },
        textAlign: { xs: 'left', sm: 'unset' },
        borderRadius: '10px',
    },
    pageTitle: {
        padding: { xs: '2rem 20px 0', sm: 0 },
    },
    divider: {
        textAlign: 'left',
        ml: '10px',
        width: '90%',
        mt: '30px',
        mb: '30px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'primary.main',
    },
    innerContainer: {
        padding: { xs: 0, sm: '0 24px' },
    },
    topContainer: {
        flexDirection: { xs: 'column-reverse', sm: 'column' },
    },
    featureSection: {
        maxWidth: { xs: '100%', sm: 'calc(80% + 30px)' },
        padding: { xs: '5px 20px 10px', sm: '0 0 30px' },
    },
    featureGridItems: {
        padding: { xs: 0, sm: '0 15px' },
        height: '100%',
    },
    lowerContainer: {
        width: { xs: '100%', sm: '80%' },
    },
    cancellationSection: {
        maxWidth: { xs: '100%', sm: 'calc(80% + 30px)' },
        padding: { xs: '0 0 20px', sm: '0 0 30px' },
    },
    cancellationtText: {
        color: 'text.soft',
    },
} as const;

interface ProductDisplayProps {
    readonly tour: Product;
}

export default function ProductDisplay({ tour }: ProductDisplayProps) {
    const productController = useContext(ProductsStore);
    const bookingController = useContext(BookingStore);

    const currentSales = () => {
        const sales = productController?.product?.sales?.filter((sale) => !moment().isAfter(sale.endDate));
        const returnValue = sales && sales.length > 0 ? sales : undefined;
        return returnValue;
    };

    useEffect(() => {
        return () => {
            bookingController.clearController();
            productController.clearController();
        };
    }, []);

    const productPath = productURL(tour);
    const productImage = tour.heroImage?.jpeg || tour.photos?.[0]?.largeURL || tour.photos?.[0]?.url;
    const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: tour.name,
        description: tour.shortDescription,
        ...(productImage && { image: [productImage] }),
        brand: { '@type': 'Brand', name: 'GSL Aviation' },
        ...(tour.displayPrice?.price && {
            offers: {
                '@type': 'Offer',
                price: tour.displayPrice.price,
                priceCurrency: 'AUD',
                availability: 'https://schema.org/InStock',
                url: `${SITE_URL}${productPath}`,
            },
        }),
    };

    const categoryLabel = tour.category
        ? `${tour.category.charAt(0).toUpperCase()}${tour.category.slice(1)} Tours`
        : 'Tours';
    const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            ...(tour.category
                ? [{ '@type': 'ListItem', position: 2, name: categoryLabel, item: `${SITE_URL}/${tour.category}` }]
                : []),
            {
                '@type': 'ListItem',
                position: tour.category ? 3 : 2,
                name: tour.name,
                item: `${SITE_URL}${productPath}`,
            },
        ],
    };

    return (
        <>
            <Seo
                title={`${tour.name} | ${tour.titleSEO}`}
                description={tour.shortDescription}
                path={productPath}
                image={productImage}
                type="product"
                jsonLd={[productSchema, breadcrumbSchema]}
            />
            <Container maxWidth="lg" sx={displayProductSx.innerContainer}>
                <Grid container sx={displayProductSx.topContainer}>
                    <Grid size={{ xs: 12 }} sx={displayProductSx.pageTitle}>
                        <PageTitle title={tour.name} tagline={tour.tagline} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <ProductImageGallery />
                    </Grid>
                </Grid>
                <Grid container spacing={0} sx={displayProductSx.productContainer}>
                    <Grid size={{ xs: 12 }}>
                        <BookingModal />
                    </Grid>
                    {currentSales() && <Sales currentSales={currentSales} />}
                    <Grid container justifyContent="center" alignItems="stretch" sx={displayProductSx.featureSection}>
                        <Grid size={{ xs: 12, sm: 6 }} sx={displayProductSx.featureGridItems}>
                            <KeyDetailWidget />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }} sx={displayProductSx.featureGridItems}>
                            <KeySellingPointsWidget />
                        </Grid>
                    </Grid>
                    <Grid container sx={[displayProductSx.lowerContainer, displayProductSx.cancellationSection]}>
                        <Paper variant="outlined" sx={displayProductSx.productDesc} style={{ width: '100%' }}>
                            <Grid container style={{ marginBottom: 10 }}>
                                <Grid size={{ xs: 'auto' }} style={{ marginRight: 12, paddingTop: 2 }}>
                                    <EventBusyIcon color="primary" />
                                </Grid>
                                <Grid container direction="column" size={{ xs: 'grow' }}>
                                    <Typography style={{ fontWeight: 500, marginBottom: 8, fontSize: 18 }}>
                                        Free Cancellation
                                    </Typography>
                                    <Typography style={{ marginBottom: 12 }} sx={displayProductSx.cancellationtText}>
                                        Cancel or change your booking up to{' '}
                                        {displayCancellation(productController?.product?.cancellationPeriod)} prior to
                                        departure.
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid size={{ xs: 'auto' }} style={{ marginRight: 12, paddingTop: 2 }}>
                                    <UpdateIcon color="primary" />
                                </Grid>
                                <Grid container direction="column" size={{ xs: 'grow' }}>
                                    <Typography style={{ fontWeight: 500, marginBottom: 8, fontSize: 18 }}>
                                        Instant Booking
                                    </Typography>
                                    <Typography sx={displayProductSx.cancellationtText}>
                                        Book online, with live availability, and instant confirmation.
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                    <Grid container sx={displayProductSx.lowerContainer}>
                        <Grid size={{ xs: 12 }}>
                            <Paper variant="outlined" sx={displayProductSx.productDesc}>
                                {productController.product?.tagline && (
                                    <Typography variant="h5" style={{ fontWeight: 500, paddingBottom: 20 }}>
                                        {productController.product.tagline}
                                    </Typography>
                                )}
                                <DisplayText text={tour.longDescription} />
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}
